import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomMeal } from '../models/CustomMeal';
import { 
  createProposal, 
  getKnownCustomMeal, 
  getKnownCustomMeals, 
  createCustomMeal,
  availableCustomMeals 
} from '../services/DataStore';
import { IconConverter } from '../utils/IconConverter';
import { useAuth } from '../contexts/AuthContext';
import { DeepLinkHandler } from '../utils/DeepLinkHandler';
import CreateCustomMeal from './CreateCustomMeal';
import { debug } from '../utils/debug';
import { GOOGLE_MAPS_CONFIG, MAP_CONTAINER_STYLE, MAP_DEFAULTS } from '../services/maps';
import { useGoogleMaps } from '../contexts/GoogleMapsContext';

const containerStyle = {
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0
};

const CreateNewProposal = ({ onClose }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isLoaded, loadError } = useGoogleMaps();
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(5.0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(() => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(Math.ceil(now.getMinutes() / 30) * 30);
    return now;
  });
  const [selectedCost, setSelectedCost] = useState(1);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [isMealManuallySelected, setIsMealManuallySelected] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [map, setMap] = useState(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const circleRef = useRef(null);
  const isInitializedRef = useRef(false);
  const isPlacesInitializedRef = useRef(false);
  const initPromiseRef = useRef(null);
  const initialLocationRef = useRef(null);
  const geocoderRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const tempProposalRef = useRef({
    location: null,
    range: null,
    eventtime: null,
    createdtime: null,
    cost: null,
    meal: null,
    state: 'waiting',
    owner: currentUser.uid,
    details: '',
    description: ''
  });
  const [showCreateCustomMeal, setShowCreateCustomMeal] = useState(false);

  const MIN_RADIUS_MILES = 1.0;
  const MAX_RADIUS_MILES = 10.0;
  const RADIUS_STEP_MILES = 0.5;
  const MILES_TO_METERS = 1609.34;

  debug.log('CreateNewProposal map loading state:', {
    isLoaded,
    hasError: !!loadError,
    errorMessage: loadError?.message
  });

  // Helper function to get meal based on time
  const getMealForTime = useCallback((time) => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const timeInMinutes = hours * 60 + minutes;

    if (timeInMinutes >= 0 && timeInMinutes < 630) { // 10:30 AM
      return getKnownCustomMeal('1'); // Breakfast
    } else if (timeInMinutes >= 630 && timeInMinutes < 990) { // 4:30 PM
      return getKnownCustomMeal('2'); // Lunch
    } else {
      return getKnownCustomMeal('3'); // Dinner
    }
  }, []);

  const updateLocationName = useCallback(async (location) => {
    if (!geocoderRef.current || !location) return;

    try {
      const response = await geocoderRef.current.geocode({
        location: location
      });

      if (response.results && response.results.length > 0) {
        const addressComponents = response.results[0].address_components;
        const cityComponent = addressComponents.find(
          component => component.types.includes('locality')
        );
        
        const locationName = cityComponent ? cityComponent.long_name : response.results[0].formatted_address;
        setLocationName(locationName);
        
        tempProposalRef.current.details = locationName;
        tempProposalRef.current.description = `${selectedMeal ? selectedMeal.getDescription() : 'Meal'}, ${selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })} near ${locationName}`;
      }
    } catch (error) {
      debug.error('Error getting location name:', error);
    }
  }, [selectedMeal, selectedDate]);

  const handleRadiusChange = (e) => {
    const newRadius = parseInt(e.target.value);
    setRadiusMiles(newRadius);
    debug.log('Updating radius');
    // Update proposal range immediately
    tempProposalRef.current.range = newRadius * MILES_TO_METERS;
  };

  const handleLocationUpdate = useCallback((newLocation) => {
    if (!mapInstanceRef.current || !newLocation) return;

    // Update temporary proposal first
    tempProposalRef.current.location = {
      latitude: newLocation.lat,
      longitude: newLocation.lng
    };
    // Use existing proposal range
    const proposalRange = tempProposalRef.current.range;

    // Then update UI state from proposal
    setCurrentLocation(newLocation);
    initialLocationRef.current = newLocation;

    // Update location name
    updateLocationName(newLocation);

    // Update circle position with animation
    if (circleRef.current) {
      // Get current circle properties
      const currentCenter = circleRef.current.getCenter();
      const currentRadius = circleRef.current.getRadius();
      
      // Animation settings
      const steps = 20;
      const latStep = (newLocation.lat - currentCenter.lat()) / steps;
      const lngStep = (newLocation.lng - currentCenter.lng()) / steps;
      const radiusStep = (proposalRange - currentRadius) / steps;
      
      let currentStep = 0;
      const animate = () => {
        if (currentStep < steps) {
          const newLat = currentCenter.lat() + (latStep * currentStep);
          const newLng = currentCenter.lng() + (lngStep * currentStep);
          const newRadius = currentRadius + (radiusStep * currentStep);
          
          circleRef.current.setCenter({ lat: newLat, lng: newLng });
          circleRef.current.setRadius(newRadius);
          
          currentStep++;
          requestAnimationFrame(animate);
        } else {
          // Ensure we end up exactly at the target values from proposal
          circleRef.current.setCenter(newLocation);
          circleRef.current.setRadius(proposalRange);

          // Get the bounds of the circle
          const bounds = circleRef.current.getBounds();
          
          // Add padding to ensure circle is fully visible
          const padding = {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50
          };
          
          // Fit the map to show the entire circle with padding
          mapInstanceRef.current.fitBounds(bounds, {
            padding: padding,
            animation: window.google.maps.Animation.SMOOTH
          });
        }
      };
      
      requestAnimationFrame(animate);
    } else {
      // Create new circle if it doesn't exist
      circleRef.current = new window.google.maps.Circle({
        strokeColor: '#0000FF',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#0000FF',
        fillOpacity: 0.35,
        map: mapInstanceRef.current,
        center: newLocation,
        radius: proposalRange,
        clickable: false
      });

      // Get the bounds of the new circle
      const bounds = circleRef.current.getBounds();
      
      // Add padding to ensure circle is fully visible
      const padding = {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      };
      
      // Fit the map to show the entire circle with padding
      mapInstanceRef.current.fitBounds(bounds, {
        padding: padding,
        animation: window.google.maps.Animation.SMOOTH
      });
    }
  }, [updateLocationName]);

  const handleDateChange = (e) => {
    const [year, month, day] = e.target.value.split('-');
    // Create a new date by combining the new date with the existing time
    // This preserves the timezone context properly
    const newDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
    setSelectedDate(newDate);
    // Update proposal with new date and time
    tempProposalRef.current.eventtime = newDate.toISOString();
    // Update description with new date
    if (tempProposalRef.current.location) {
      tempProposalRef.current.description = `${selectedMeal ? selectedMeal.getDescription() : 'Meal'}, ${newDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })} near ${locationName}`;
    }
  };

  const handleTimeChange = (e) => {
    const [hours, minutes] = e.target.value.split(':');
    // Create a new date by combining the selected date with the new time
    // This preserves the timezone context properly
    const newTime = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), parseInt(hours), parseInt(minutes), 0, 0);
    setSelectedTime(newTime);
    
    // Update proposal with new time
    tempProposalRef.current.eventtime = newTime.toISOString();
    
    // Auto-update meal if not manually selected
    if (!isMealManuallySelected) {
      const newMeal = getMealForTime(newTime);
      setSelectedMeal(newMeal);
      tempProposalRef.current.meal = newMeal.toString();
    }
    
    // Update description with new time
    if (tempProposalRef.current.location) {
      tempProposalRef.current.description = `${selectedMeal ? selectedMeal.getDescription() : 'Meal'}, ${newTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })} near ${locationName}`;
    }
  };

  const handleCostChange = (cost) => {
    setSelectedCost(cost);
    // Update proposal with new cost as a number
    tempProposalRef.current.cost = cost;
  };

  const handleMealChange = (meal) => {
    setSelectedMeal(meal);
    setIsMealManuallySelected(true);
    // Update proposal with new meal
    tempProposalRef.current.meal = meal.toString();
    // Update description with new meal
    if (tempProposalRef.current.location) {
      tempProposalRef.current.description = `${meal.getDescription()}, ${selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })} near ${locationName}`;
    }
  };

  const handleCreateProposal = async () => {
    if (!currentLocation || !selectedMeal) {
      alert('Please select a location and meal type');
      return;
    }

    setIsLoading(true);
    try {
      // Ensure cost and createdtime are set
      tempProposalRef.current.cost = selectedCost;
      tempProposalRef.current.createdtime = new Date().toISOString();
      
      debug.log('Creating proposal with eventtime:', tempProposalRef.current.eventtime);
      debug.log('Creating proposal with createdtime:', tempProposalRef.current.createdtime);
      debug.log('Creating proposal with cost:', tempProposalRef.current.cost);
      
      const createdProposal = await createProposal(tempProposalRef.current);
      debug.log('Proposal created await:', createdProposal);
      
      if (createdProposal) {
        debug.log('Proposal created non null:');
        onClose(createdProposal);
        navigate(`/proposal/${createdProposal.id}`);
      }
    } catch (error) {
      debug.error('Error creating proposal:', error);
      alert('Failed to create proposal: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMealCreated = async (customMeal) => {
    try {
      const updatedUser = await createCustomMeal(customMeal.name, customMeal.toString());
      const customMeals = updatedUser.getCustomMeals();
      const createdMeal = customMeals.find(meal => meal.name === customMeal.name);
      
      if (!createdMeal) {
        debug.error('Created meal not found in updated user data');
      } else {
        // Set the newly created meal as the selected meal
        handleMealChange(createdMeal);
      }
    } catch (error) {
      debug.error('Error creating custom meal:', error);
      alert('Error saving custom meal: ' + error.message);
    }
  };

  // Initialize proposal with default time and meal
  useEffect(() => {
    const defaultTime = new Date();
    defaultTime.setHours(defaultTime.getHours() + 1);
    defaultTime.setMinutes(Math.ceil(defaultTime.getMinutes() / 30) * 30);
    
    setSelectedTime(defaultTime);
    const defaultMeal = getMealForTime(defaultTime);
    if (defaultMeal) {
      setSelectedMeal(defaultMeal);
      tempProposalRef.current.meal = defaultMeal.toString();
      tempProposalRef.current.eventtime = defaultTime.toISOString();
      tempProposalRef.current.createdtime = new Date().toISOString();
      tempProposalRef.current.cost = 1;
    }
  }, [getMealForTime]);

  // Get initial location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          // Update proposal first
          tempProposalRef.current.location = {
            latitude: newLocation.lat,
            longitude: newLocation.lng
          };
          tempProposalRef.current.range = radiusMiles * MILES_TO_METERS;
          // Then update UI state
          initialLocationRef.current = newLocation;
          // If map is already initialized, update location
          if (mapInstanceRef.current) {
            handleLocationUpdate(newLocation);
          }
        },
        (error) => {
          debug.error('Error getting location:', error);
          // Fallback to San Francisco
          const fallbackLocation = { lat: 37.7749, lng: -122.4194 };
          // Update proposal first
          tempProposalRef.current.location = {
            latitude: fallbackLocation.lat,
            longitude: fallbackLocation.lng
          };
          tempProposalRef.current.range = radiusMiles * MILES_TO_METERS;
          // Then update UI state
          initialLocationRef.current = fallbackLocation;
          // If map is already initialized, update location
          if (mapInstanceRef.current) {
            handleLocationUpdate(fallbackLocation);
          }
        }
      );
    } else {
      // Fallback to San Francisco if geolocation is not supported
      const fallbackLocation = { lat: 37.7749, lng: -122.4194 };
      // Update proposal first
      tempProposalRef.current.location = {
        latitude: fallbackLocation.lat,
        longitude: fallbackLocation.lng
      };
      tempProposalRef.current.range = radiusMiles * MILES_TO_METERS;
      // Then update UI state
      initialLocationRef.current = fallbackLocation;
      // If map is already initialized, update location
      if (mapInstanceRef.current) {
        handleLocationUpdate(fallbackLocation);
      }
    }
  }, []); // Remove handleLocationUpdate from dependencies to prevent re-runs

  // Initialize map
  useEffect(() => {
    let isMounted = true;
    let debounceTimer;

    const initMap = async () => {
      if (!isLoaded || !mapContainerRef.current || isInitializedRef.current || !isMounted) {
        return;
      }

      try {
        const { Map } = await window.google.maps.importLibrary("maps");
        const { Geocoder } = await window.google.maps.importLibrary("geocoding");
        
        if (!isMounted) return;

        // Initialize geocoder
        geocoderRef.current = new Geocoder();

        // Use proposal location if available, otherwise use initial location
        const initialCenter = tempProposalRef.current.location ? {
          lat: tempProposalRef.current.location.latitude,
          lng: tempProposalRef.current.location.longitude
        } : (initialLocationRef.current || { lat: 37.7749, lng: -122.4194 });

        const mapInstance = new Map(mapContainerRef.current, {
          mapId: "4f8a2d2881ee203f",
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          center: initialCenter,
          zoom: 12
        });

        if (!isMounted) {
          mapInstance.setMap(null);
          return;
        }

        mapInstanceRef.current = mapInstance;
        setMap(mapInstance);
        isInitializedRef.current = true;

        // If we have a proposal location, update the map
        if (tempProposalRef.current.location) {
          handleLocationUpdate({
            lat: tempProposalRef.current.location.latitude,
            lng: tempProposalRef.current.location.longitude
          });
        }

        // Initialize Places Autocomplete if not already initialized
        if (!isPlacesInitializedRef.current) {
          try {
            await window.google.maps.importLibrary("places");
            
            const searchInput = document.getElementById('place-autocomplete-input');
            if (searchInput) {
              debug.log('Initializing Places Autocomplete...');
              
              // Clean up any existing pac-container
              const existingPacContainer = document.querySelector('.pac-container');
              if (existingPacContainer) {
                existingPacContainer.remove();
              }

              // Create results container
              const resultsContainer = document.createElement('div');
              resultsContainer.className = 'absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg max-h-60 overflow-y-auto';
              resultsContainer.style.display = 'none';
              searchInput.parentElement.appendChild(resultsContainer);
              resultsContainerRef.current = resultsContainer;

              // Set up the autocomplete input handler
              searchInput.addEventListener('input', async (e) => {
                const input = e.target.value;
                
                // Clear previous timer
                if (debounceTimer) {
                  clearTimeout(debounceTimer);
                }

                // Set new timer
                debounceTimer = setTimeout(async () => {
                  if (!input.trim()) {
                    setSearchResults([]);
                    if (resultsContainerRef.current) {
                      resultsContainerRef.current.style.display = 'none';
                    }
                    return;
                  }

                  try {
                    const bounds = mapInstanceRef.current.getBounds();
                    const center = bounds.getCenter();
                    
                    const requestBody = {
                      input: input,
                      locationBias: {
                        circle: {
                          center: {
                            latitude: center.lat(),
                            longitude: center.lng()
                          },
                          radius: 500.0
                        }
                      }
                    };

                    const requestHeaders = {
                      'Content-Type': 'application/json',
                      'X-Goog-Api-Key': GOOGLE_MAPS_CONFIG.apiKey,
                      'X-Goog-FieldMask': 'suggestions.placePrediction.text'
                    };

                    debug.log('Places API Request:', {
                      url: 'https://places.googleapis.com/v1/places:autocomplete',
                      method: 'POST',
                      headers: requestHeaders,
                      body: requestBody
                    });

                    const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
                      method: 'POST',
                      headers: requestHeaders,
                      body: JSON.stringify(requestBody)
                    });

                    if (!response.ok) {
                      const errorData = await response.json();
                      debug.error('Places API error:', errorData);
                      throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    debug.log('Autocomplete response:', data);

                    if (data.suggestions) {
                      const results = data.suggestions.map(suggestion => ({
                        text: suggestion.placePrediction.text.text
                      }));
                      setSearchResults(results);
                      
                      // Update results display
                      if (resultsContainerRef.current) {
                        resultsContainerRef.current.innerHTML = '';
                        if (results.length === 0) {
                          resultsContainerRef.current.style.display = 'none';
                        } else {
                          results.forEach(result => {
                            const div = document.createElement('div');
                            div.className = 'p-2 hover:bg-gray-100 cursor-pointer';
                            div.textContent = result.text;
                            div.addEventListener('click', async () => {
                              setSearchQuery(result.text);
                              resultsContainerRef.current.style.display = 'none';

                              // Get place details
                              try {
                                const geocoder = new window.google.maps.Geocoder();
                                const response = await geocoder.geocode({
                                  address: result.text
                                });

                                if (response.results && response.results.length > 0) {
                                  const location = response.results[0].geometry.location;
                                  const newLocation = {
                                    lat: location.lat(),
                                    lng: location.lng()
                                  };
                                  // Update initial location reference before updating map
                                  initialLocationRef.current = newLocation; 
                                  // Update map location using our existing helper
                                  handleLocationUpdate(newLocation);
                                }
                              } catch (error) {
                                debug.error('Error getting place details:', error);
                              }
                            });
                            resultsContainerRef.current.appendChild(div);
                          });
                          resultsContainerRef.current.style.display = 'block';
                        }
                      }
                    }
                  } catch (error) {
                    debug.error('Error fetching autocomplete suggestions:', error);
                    setSearchResults([]);
                    if (resultsContainerRef.current) {
                      resultsContainerRef.current.style.display = 'none';
                    }
                  }
                }, 300); // 300ms debounce
              });

              isPlacesInitializedRef.current = true;
              debug.log('Places Autocomplete initialized');
            }
          } catch (error) {
            debug.error('Error initializing Places Autocomplete:', error);
          }
        }

        // Add click listener
        mapInstance.addListener('click', (e) => {
          if (!isMounted) return;
          handleLocationUpdate({
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
          });
        });

        // Add reset view button
        const resetButton = document.createElement('div');
        resetButton.className = 'absolute bottom-4 left-4 z-10';
        resetButton.innerHTML = `
          <button class="bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors" title="Reset to My Location">
            <span class="material-symbols-outlined text-gray-600">my_location</span>
          </button>
        `;
        resetButton.addEventListener('click', () => {
          if (!isMounted) return;
          
          // Simply center the map on the current circle position
          if (circleRef.current) {
            const center = circleRef.current.getCenter();
            mapInstanceRef.current.panTo(center, { animation: window.google.maps.Animation.SMOOTH });
            
            // Calculate appropriate zoom level based on current radius
            const currentRadius = circleRef.current.getRadius();
            const currentRadiusMiles = currentRadius / MILES_TO_METERS;
            const zoomLevel = Math.max(10, 15 - Math.log2(currentRadiusMiles));
            mapInstanceRef.current.setZoom(zoomLevel, { animation: window.google.maps.Animation.SMOOTH });
          }
        });
        mapContainerRef.current.appendChild(resetButton);

      } catch (error) {
        debug.error('Error initializing map:', error);
      }
    };

    initMap();

    return () => {
      isMounted = false;
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (resultsContainerRef.current) {
        resultsContainerRef.current.remove();
        resultsContainerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (mapInstanceRef.current) {
        if (typeof mapInstanceRef.current.setMap === 'function') {
          mapInstanceRef.current.setMap(null);
        }
        mapInstanceRef.current = null;
      }
      if (mapContainerRef.current) {
        mapContainerRef.current.innerHTML = '';
      }
      isInitializedRef.current = false;
      isPlacesInitializedRef.current = false;
    };
  }, [isLoaded]); // Remove handleLocationUpdate from dependencies

  // Update the radius change effect to use smooth animations
  useEffect(() => {
    if (!mapInstanceRef.current || !circleRef.current || !tempProposalRef.current.location) return;

    debug.log('Updating radius');
    
    // Get current circle properties
    const currentRadius = circleRef.current.getRadius();
    const targetRadius = tempProposalRef.current.range;
    const currentLocation = tempProposalRef.current.location;
    
    // Animation settings
    const steps = 20;
    const radiusStep = (targetRadius - currentRadius) / steps;
    
    let currentStep = 0;
    const animate = () => {
      if (currentStep < steps) {
        const newRadius = currentRadius + (radiusStep * currentStep);
        // Ensure circle stays centered on proposal location
        circleRef.current.setCenter({
          lat: currentLocation.latitude,
          lng: currentLocation.longitude
        });
        circleRef.current.setRadius(newRadius);
        
        currentStep++;
        requestAnimationFrame(animate);
      } else {
        // Ensure we end up exactly at the target radius and correct position
        circleRef.current.setCenter({
          lat: currentLocation.latitude,
          lng: currentLocation.longitude
        });
        circleRef.current.setRadius(targetRadius);
        
        // Get the bounds of the circle
        const bounds = circleRef.current.getBounds();
        
        // Add padding to ensure circle is fully visible
        const padding = {
          top: 50,
          right: 50,
          bottom: 50,
          left: 50
        };
        
        // Fit the map to show the entire circle with padding
        mapInstanceRef.current.fitBounds(bounds, {
          padding: padding,
          animation: window.google.maps.Animation.SMOOTH
        });
      }
    };
    
    // Start the animation
    requestAnimationFrame(animate);
  }, [radiusMiles]);

  const getCostSymbol = (count) => {
    // Get user's locale
    const locale = navigator.language || 'en-US';
    const currency = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' })
      .format(0)
      .replace(/[\d,]/g, '')
      .trim();
    
    return currency.repeat(count);
  };

  if (loadError) {
    debug.error('Error loading Google Maps:', loadError);
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Map</h2>
          <p className="text-gray-600 mb-4">Unable to load the map. Please try again later.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-accent text-white px-4 py-2 rounded hover:bg-opacity-90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with event details */}
      <div className="bg-accent text-white p-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">
              Create Event{locationName ? ` - ${locationName}` : ''}
            </h1>
          </div>
          <button
            onClick={() => onClose()}
            className="text-white hover:text-gray-200"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-700">Creating your event...</p>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex min-h-0">
        {/* Left panel - Map */}
        <div className="flex-1 min-h-0 relative">
          {/* Search Field Overlay */}
          <div className="absolute top-4 left-4 right-4 z-10">
            <div className="relative">
              <input
                type="text"
                id="place-autocomplete-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Find a place"
                className="w-full p-3 border border-gray-300 rounded-lg focus:border-accent focus:ring-1 focus:ring-accent bg-white shadow-lg"
              />
            </div>
          </div>

          {!isLoaded ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div 
              ref={mapContainerRef} 
              className="w-full h-full"
              style={MAP_CONTAINER_STYLE}
            />
          )}
        </div>

        {/* Right panel - Controls */}
        <div className="w-96 border-l border-gray-200 flex flex-col">
          <div className="p-4 flex-1 overflow-y-auto">
            {/* Controls Container */}
            <div className="space-y-4">
              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={selectedDate.toISOString().split('T')[0]}
                    onChange={handleDateChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-accent focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <input
                    type="time"
                    value={`${selectedTime.getHours().toString().padStart(2, '0')}:${selectedTime.getMinutes().toString().padStart(2, '0')}`}
                    onChange={handleTimeChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-accent focus:ring-1 focus:ring-accent"
                  />
                </div>
              </div>

              {/* Cost and Meal Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        const menu = document.createElement('div');
                        menu.className = 'absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg py-1';
                        menu.style.top = '100%';
                        menu.style.left = '0';
                        
                        [1, 2, 3, 4].forEach(cost => {
                          const button = document.createElement('button');
                          button.className = 'w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center';
                          button.innerHTML = `
                            <span class="text-lg font-medium">${getCostSymbol(cost)}</span>
                            <span class="ml-2 text-sm text-gray-500">${cost === 1 ? 'Budget' : cost === 2 ? 'Moderate' : cost === 3 ? 'Expensive' : 'Luxury'}</span>
                          `;
                          button.onclick = () => {
                            handleCostChange(cost);
                            menu.remove();
                          };
                          menu.appendChild(button);
                        });

                        const container = e.currentTarget.parentElement;
                        container.appendChild(menu);

                        const handleClickOutside = (e) => {
                          if (!container.contains(e.target)) {
                            menu.remove();
                            document.removeEventListener('click', handleClickOutside);
                          }
                        };

                        setTimeout(() => {
                          document.addEventListener('click', handleClickOutside);
                        }, 0);
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg text-left hover:bg-gray-50 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent flex items-center justify-between"
                    >
                      <span className="text-lg font-medium">{getCostSymbol(selectedCost)}</span>
                      <span className="material-symbols-outlined text-gray-400">expand_more</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        const menu = document.createElement('div');
                        menu.className = 'absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg py-1';
                        menu.style.top = '100%';
                        menu.style.left = '0';
                        
                        // Add each meal to the menu
                        let mealCount = 0;
                        availableCustomMeals.forEach(meal => {
                          const button = document.createElement('button');
                          button.className = 'w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center';
                          button.innerHTML = `
                            <span class="material-symbols-outlined mr-2">${IconConverter.sfToMaterial(meal.icon)}</span>
                            <span>${meal.name}</span>
                          `;
                          button.onclick = () => {
                            handleMealChange(meal);
                            menu.remove();
                          };
                          menu.appendChild(button);
                          
                          // Add separator after the first 3 built-in meals
                          mealCount++;
                          if (mealCount === 3) {
                            const separator1 = document.createElement('div');
                            separator1.className = 'border-t border-gray-200 my-1';
                            menu.appendChild(separator1);
                          }
                        });

                        // Add separator before New option
                        const separator2 = document.createElement('div');
                        separator2.className = 'border-t border-gray-200 my-1';
                        menu.appendChild(separator2);

                        // Add New... option
                        const newButton = document.createElement('button');
                        newButton.className = 'w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center text-accent';
                        newButton.innerHTML = `
                          <span class="material-symbols-outlined mr-2">add_circle</span>
                          <span>New...</span>
                        `;
                        newButton.onclick = () => {
                          setShowCreateCustomMeal(true);
                          menu.remove();
                        };
                        menu.appendChild(newButton);

                        const container = e.currentTarget.parentElement;
                        container.appendChild(menu);

                        const handleClickOutside = (e) => {
                          if (!container.contains(e.target)) {
                            menu.remove();
                            document.removeEventListener('click', handleClickOutside);
                          }
                        };

                        setTimeout(() => {
                          document.addEventListener('click', handleClickOutside);
                        }, 0);
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg text-left hover:bg-gray-50 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        {selectedMeal ? (
                          <>
                            <span className="material-symbols-outlined mr-2">{selectedMeal.getIconName()}</span>
                            <span>{selectedMeal.getDescription()}</span>
                          </>
                        ) : (
                          <span className="text-gray-500">Select Meal</span>
                        )}
                      </div>
                      <span className="material-symbols-outlined text-gray-400">expand_more</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Range Slider */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Radius: {radiusMiles.toFixed(1)} miles
                </label>
                <input
                  type="range"
                  min="0"
                  max={Math.floor((MAX_RADIUS_MILES - MIN_RADIUS_MILES) / RADIUS_STEP_MILES)}
                  value={(radiusMiles - MIN_RADIUS_MILES) / RADIUS_STEP_MILES}
                  onChange={handleRadiusChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{MIN_RADIUS_MILES} mi</span>
                  <span>{MAX_RADIUS_MILES} mi</span>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleCreateProposal}
                className="w-full bg-accent text-white p-3 rounded-lg hover:bg-accent-focus transition-colors"
              >
                Create Event
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create Custom Meal Popup */}
      {showCreateCustomMeal && (
        <CreateCustomMeal
          onClose={() => setShowCreateCustomMeal(false)}
          onMealCreated={handleMealCreated}
          location={currentLocation}
        />
      )}
    </div>
  );
};

export default CreateNewProposal;